import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import DriverAssignment from '../components/Courier/DriverAssignment';
import { API_ENDPOINTS } from '../config/api';

interface CourierCompany {
  id: string;
  name: string;
  business_registration: string;
  contact_person: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  is_active: boolean;
  verification_status: 'pending' | 'verified' | 'rejected';
  verification_notes?: string;
  created_at: string;
  updated_at: string;
}

const AdminCourierManagement: React.FC = () => {
  const { user } = useAuth();
  const [couriers, setCouriers] = useState<CourierCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCourier, setSelectedCourier] = useState<CourierCompany | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationData, setVerificationData] = useState({
    status: 'pending' as 'pending' | 'verified' | 'rejected',
    notes: ''
  });

  // Filter states
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'verified' | 'rejected'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourierForDrivers, setSelectedCourierForDrivers] = useState<CourierCompany | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchCouriers();
    }
  }, [user]);

  const fetchCouriers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch couriers');
      }

      const data = await response.json();
      setCouriers(data);
    } catch (error) {
      console.error('Error fetching couriers:', error);
      toast.error('Failed to load courier companies');

      // Fallback to mock data for development
      const mockCouriers: CourierCompany[] = [
        {
          id: '1',
          name: 'FastTrack Express',
          business_registration: 'BW123456789',
          contact_person: 'John Mokoena',
          email: 'john@fasttrack.bw',
          phone: '+267 71 234 567',
          address: '123 Main Street',
          city: 'Gaborone',
          country: 'Botswana',
          is_active: true,
          verification_status: 'pending',
          created_at: '2024-12-01T10:00:00Z',
          updated_at: '2024-12-01T10:00:00Z'
        },
        {
          id: '2',
          name: 'SpeedCourier BW',
          business_registration: 'BW987654321',
          contact_person: 'Sarah Kgosi',
          email: 'sarah@speedcourier.bw',
          phone: '+267 72 345 678',
          address: '456 Commerce Way',
          city: 'Francistown',
          country: 'Botswana',
          is_active: true,
          verification_status: 'verified',
          verification_notes: 'All documents verified successfully',
          created_at: '2024-12-01T09:00:00Z',
          updated_at: '2024-12-01T11:00:00Z'
        },
        {
          id: '3',
          name: 'Reliable Delivery',
          business_registration: 'BW456789123',
          contact_person: 'David Tshwene',
          email: 'david@reliable.bw',
          phone: '+267 73 456 789',
          address: '789 Industrial Ave',
          city: 'Maun',
          country: 'Botswana',
          is_active: false,
          verification_status: 'rejected',
          verification_notes: 'Business registration expired',
          created_at: '2024-12-01T08:00:00Z',
          updated_at: '2024-12-01T12:00:00Z'
        }
      ];
      setCouriers(mockCouriers);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationUpdate = async () => {
    if (!selectedCourier) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/admin/${selectedCourier.id}/verify`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verificationStatus: verificationData.status,
          verificationNotes: verificationData.notes
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update verification status');
      }

      // Update local state
      setCouriers(prev => prev.map(courier =>
        courier.id === selectedCourier.id
          ? {
              ...courier,
              verification_status: verificationData.status,
              verification_notes: verificationData.notes,
              updated_at: new Date().toISOString()
            }
          : courier
      ));

      toast.success(`Courier verification status updated to ${verificationData.status}`);
      setShowVerificationModal(false);
      setSelectedCourier(null);
      setVerificationData({ status: 'pending', notes: '' });

      // Refresh courier data to ensure we have the latest from database
      fetchCouriers();
    } catch (error) {
      console.error('Error updating verification status:', error);
      toast.error('Failed to update verification status');
    }
  };

  const openVerificationModal = (courier: CourierCompany) => {
    setSelectedCourier(courier);
    setVerificationData({
      status: courier.verification_status,
      notes: courier.verification_notes || ''
    });
    setShowVerificationModal(true);
  };

  const toggleCourierStatus = async (courierId: string, currentStatus: boolean) => {
    try {
      // This would be an API call to toggle courier status
      setCouriers(prev => prev.map(courier =>
        courier.id === courierId
          ? { ...courier, is_active: !currentStatus }
          : courier
      ));

      toast.success(`Courier ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error toggling courier status:', error);
      toast.error('Failed to update courier status');
    }
  };

  // Filter couriers based on status and search
  const filteredCouriers = couriers.filter(courier => {
    const matchesStatus = statusFilter === 'all' || courier.verification_status === statusFilter;
    const matchesSearch = courier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         courier.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         courier.business_registration.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Get status badge styling
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'verified': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return statusConfig[status as keyof typeof statusConfig] || 'bg-gray-100 text-gray-800';
  };

  // Get active status badge
  const getActiveBadge = (isActive: boolean) => {
    return isActive
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-primary-700 mb-4">Access Denied</h1>
          <p className="text-primary-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-primary-600">Loading courier companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="section-header primary">Courier Company Management</h1>
          <p className="text-primary-600 mt-2">Manage and verify courier company registrations</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="stats-card">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-lg">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-primary-600">Total Couriers</p>
                <p className="text-2xl font-semibold text-primary-800">{couriers.length}</p>
              </div>
            </div>
          </div>

          <div className="card-secondary">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Pending Review</p>
                <p className="text-2xl font-semibold text-secondary-800">
                  {couriers.filter(c => c.verification_status === 'pending').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card-tertiary">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-tertiary-600">Verified</p>
                <p className="text-2xl font-semibold text-tertiary-800">
                  {couriers.filter(c => c.verification_status === 'verified').length}
                </p>
              </div>
            </div>
          </div>

          <div className="card-neutral">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Rejected</p>
                <p className="text-2xl font-semibold text-gray-800">
                  {couriers.filter(c => c.verification_status === 'rejected').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="card-highlighted mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search couriers by name, email, or business registration..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field w-full"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="input-field"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Couriers Table */}
        <div className="card-highlighted">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-primary-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="table-header">Company</th>
                  <th className="table-header">Contact</th>
                  <th className="table-header">Location</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Verification</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-primary-200">
                {filteredCouriers.map((courier) => (
                  <tr key={courier.id} className="hover:bg-primary-50">
                    <td className="table-cell">
                      <div>
                        <div className="font-medium text-primary-900">{courier.name}</div>
                        <div className="text-sm text-primary-500">#{courier.business_registration}</div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div>
                        <div className="text-sm text-primary-900">{courier.contact_person}</div>
                        <div className="text-sm text-primary-500">{courier.email}</div>
                        <div className="text-sm text-primary-500">{courier.phone}</div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <div>
                        <div className="text-sm text-primary-900">{courier.city}</div>
                        <div className="text-sm text-primary-500">{courier.address}</div>
                      </div>
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActiveBadge(courier.is_active)}`}>
                        {courier.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(courier.verification_status)}`}>
                        {courier.verification_status.charAt(0).toUpperCase() + courier.verification_status.slice(1)}
                      </span>
                    </td>
                    <td className="table-cell">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openVerificationModal(courier)}
                          className="btn-secondary text-xs"
                        >
                          {courier.verification_status === 'pending' ? 'Review' : 'Update'}
                        </button>
                        <button
                          onClick={() => setSelectedCourierForDrivers(courier)}
                          className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 hover:bg-blue-200"
                        >
                          Manage Drivers
                        </button>
                        <button
                          onClick={() => toggleCourierStatus(courier.id, courier.is_active)}
                          className={`text-xs px-2 py-1 rounded ${
                            courier.is_active
                              ? 'bg-red-100 text-red-800 hover:bg-red-200'
                              : 'bg-green-100 text-green-800 hover:bg-green-200'
                          }`}
                        >
                          {courier.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredCouriers.length === 0 && (
            <div className="text-center py-8">
              <p className="text-primary-500">No courier companies found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && selectedCourier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-primary-800 mb-4">
              Update Verification Status
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Company: {selectedCourier.name}
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Verification Status
              </label>
              <select
                value={verificationData.status}
                onChange={(e) => setVerificationData(prev => ({ ...prev, status: e.target.value as any }))}
                className="input-field w-full"
              >
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Notes
              </label>
              <textarea
                value={verificationData.notes}
                onChange={(e) => setVerificationData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                className="input-field w-full"
                placeholder="Add verification notes..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowVerificationModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleVerificationUpdate}
                className="btn-primary"
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Management Modal */}
      {selectedCourierForDrivers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-primary-800">
                  Driver Management - {selectedCourierForDrivers.name}
                </h3>
                <button
                  onClick={() => setSelectedCourierForDrivers(null)}
                  className="text-primary-600 hover:text-primary-800"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <DriverAssignment
                companyId={selectedCourierForDrivers.id}
                companyName={selectedCourierForDrivers.name}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCourierManagement;
